/**
 * Routines — a client's recurring tasks (prototype routineTab / everyPicker / addRoutine, docs/prototype.html
 * L1795–L1835) and "For many clients" (bulkRoutine / bkPick / bkFile / bkRead / bkApply, L2681–L2715).
 *
 * Reads and writes go through lib/checklists.ts: GET/POST /clients/{id}/routines, PATCH/DELETE
 * /clients/{id}/routines/{rid} (If-Match = routine version) and POST /routines/bulk (pick, or sheet with a dry run
 * first). The server owns the when-rules (routineWhenIssues), duplicates (409 / `already_has_it`) and scope (a
 * member's non-own clients are skipped); this file only sends the fields that apply and shows what comes back.
 */
import { useMemo, useState } from 'react';
import {
  ROUTINE_EVERY,
  ROUTINE_EVERY_LABEL,
  Segment,
  type BulkRoutinesResponse,
  type ClientRoutine,
  type ClientRow,
  type RoutineEvery,
} from '@amplispot/contracts';
import { newIdempotencyKey } from '../../api';
import { STATE_CHIP, useBulkRoutines, useCreateRoutine, useRemoveRoutine, useRoutines, useUpdateRoutine } from '../../lib/checklists';
import { useClients } from '../../lib/data';
import { firstName } from '../../lib/format';
import { useSession } from '../../lib/session';
import { readSheet, routineSheetRows, routineTemplateCsv, type SheetRow } from '../../lib/sheet';
import { Drawer, Empty, ErrorState, Field, Loading, Modal, Seg, useFail } from '../../ui/kit';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] as const;
type Weekday = (typeof WEEKDAYS)[number];

// ---------------------------------------------------------------- shared: clients in scope, the ▲ mark

/** Clients the caller may set routines and checklists up for: any (checklist.write.any) or the ones they are SPOC of. */
export function useScopeClients() {
  const { me, can } = useSession();
  const q = useClients();
  const any = can('checklist.write.any');
  const data = useMemo(() => (q.data ?? []).filter((c) => any || c.spoc.id === me.user.id), [q.data, any, me.user.id]);
  return { ...q, data, any };
}

/** Prototype `prioDot`: a small ▲ beside a high-priority client's name. */
export function PrioMark({ priority }: { priority: string }) {
  return priority === 'high' ? (
    <span className="pm" title="High priority client">
      ▲{' '}
    </span>
  ) : null;
}

// ---------------------------------------------------------------- how often + on which day

export interface When {
  every: RoutineEvery;
  weekday: Weekday;
  day: string;
  start: 'this' | 'next';
}
export const NEW_WHEN: When = { every: 'week', weekday: 'Mon', day: '15', start: 'this' };

/** Only the fields that apply to `every` (the server's routineWhenIssues refuses the others). */
export function whenBody(w: When) {
  if (w.every === 'day') return { every: w.every } as const;
  if (w.every === 'month') return { every: w.every, day_of_month: Number(w.day) } as const;
  if (w.every === 'fortnight') return { every: w.every, weekday: w.weekday, start_phase: w.start } as const;
  return { every: w.every, weekday: w.weekday } as const;
}

/** "Must be a day 1–28" before sending, so the obvious slip never makes a round trip. */
function dayIssue(w: When): string | undefined {
  if (w.every !== 'month') return undefined;
  const n = Number(w.day);
  return Number.isInteger(n) && n >= 1 && n <= 28 ? undefined : 'Pick a day from 1 to 28';
}

/** Prototype everyPicker: How often, then On (none for daily, a weekday, a day of the month; Starting for every 2 weeks). */
export function EveryPicker({ value, onChange, errs }: { value: When; onChange: (w: When) => void; errs: Record<string, string> }) {
  const set = (p: Partial<When>) => onChange({ ...value, ...p });
  return (
    <div className="cols c2">
      <Field label="How often" error={errs.every}>
        <select className="in" value={value.every} onChange={(e) => set({ every: e.target.value as RoutineEvery })}>
          {ROUTINE_EVERY.map((k) => (
            <option key={k} value={k}>
              {ROUTINE_EVERY_LABEL[k]}
            </option>
          ))}
        </select>
      </Field>
      <div>
        {value.every === 'day' ? (
          <Field label="On">
            <input className="in" value="Every working day" disabled />
          </Field>
        ) : value.every === 'month' ? (
          <Field label="On day of the month" error={errs.day_of_month}>
            <input className="in" type="number" min={1} max={28} value={value.day} onChange={(e) => set({ day: e.target.value })} aria-invalid={!!errs.day_of_month || undefined} />
          </Field>
        ) : (
          <>
            <Field label="On" error={errs.weekday}>
              <select className="in" value={value.weekday} onChange={(e) => set({ weekday: e.target.value as Weekday })}>
                {WEEKDAYS.map((d) => (
                  <option key={d}>{d}</option>
                ))}
              </select>
            </Field>
            {value.every === 'fortnight' ? (
              <Field label="Starting" error={errs.start_phase}>
                <select className="in" value={value.start} onChange={(e) => set({ start: e.target.value as 'this' | 'next' })}>
                  <option value="this">This week</option>
                  <option value="next">Next week</option>
                </select>
              </Field>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

const WHEN_FIELDS = ['what', 'every', 'weekday', 'day_of_month', 'start_phase', 'client_ids'] as const;

// ---------------------------------------------------------------- Add a recurring task

export function AddRoutineModal({ client, onClose }: { client: { id: string; name: string }; onClose: () => void }) {
  const create = useCreateRoutine();
  const { errs, setErrs, fail, toast } = useFail(WHEN_FIELDS);
  const [key] = useState(newIdempotencyKey);
  const [what, setWhat] = useState('');
  const [when, setWhen] = useState<When>(NEW_WHEN);

  const submit = () => {
    const local: Record<string, string> = {};
    if (what.trim().length < 3) local.what = 'Say what the SPOC does (at least 3 characters)';
    const d = dayIssue(when);
    if (d) local.day_of_month = d;
    setErrs(local);
    if (Object.keys(local).length) return;
    create.mutate(
      { clientId: client.id, key, body: { what: what.trim(), ...whenBody(when) } },
      {
        onSuccess: () => {
          toast(`Added. Repeats ${ROUTINE_EVERY_LABEL[when.every].toLowerCase()}.`);
          onClose();
        },
        onError: fail,
      },
    );
  };

  return (
    <Modal
      title="Add a recurring task"
      sub={`${client.name}. It repeats on its own and shows in the SPOC’s plan when it is due.`}
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add'}
          </button>
        </>
      }
    >
      <Field label="What does the SPOC do?" error={errs.what}>
        <input
          className="in"
          value={what}
          onChange={(e) => {
            setWhat(e.target.value);
            if (errs.what) setErrs(({ what: _w, ...rest }) => rest);
          }}
          placeholder="e.g. Call the client about the weekly MIS"
          maxLength={200}
          aria-invalid={!!errs.what || undefined}
          autoFocus
        />
      </Field>
      <EveryPicker value={when} onChange={setWhen} errs={errs} />
    </Modal>
  );
}

// ---------------------------------------------------------------- Routine for <client>

function stateOf(r: ClientRoutine): [string, string] {
  if (r.current) return [STATE_CHIP[r.current.state], r.current.label];
  return ['info', r.start_phase === 'next' ? 'Starts next week' : 'Not due yet'];
}

function RoutineRow({ r, clientId, edit }: { r: ClientRoutine; clientId: string; edit: boolean }) {
  const update = useUpdateRoutine();
  const remove = useRemoveRoutine();
  const { fail, toast } = useFail([]);
  const [day, setDay] = useState(String(r.day_of_month ?? 15));
  const [st, label] = stateOf(r);
  const busy = update.isPending || remove.isPending;

  const patch = (body: Parameters<typeof update.mutate>[0]['body']) => update.mutate({ clientId, id: r.id, version: r.version, body }, { onError: fail });
  const setEvery = (v: RoutineEvery) => {
    if (v === r.every) return;
    if (v === 'month') {
      setDay(String(r.day_of_month ?? 15));
      patch({ every: v, day_of_month: r.day_of_month ?? 15 });
    } else if (v === 'week' || v === 'fortnight') patch({ every: v, weekday: r.weekday ?? 'Mon' });
    else patch({ every: v });
  };
  const commitDay = () => {
    const n = Number(day);
    if (!Number.isInteger(n) || n < 1 || n > 28) {
      toast('Pick a day from 1 to 28');
      setDay(String(r.day_of_month ?? 15));
      return;
    }
    if (n !== r.day_of_month) patch({ day_of_month: n });
  };

  // The design's single line (prototype routineTab) crushes the title to one word per line inside the 520px
  // drawer's 2:1 columns; the title keeps a readable width and the controls wrap under it instead.
  return (
    <div className="li" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <div className="b" style={{ minWidth: 160 }}>
        <div className="t">{r.what}</div>
        <div className="m">
          <span>{r.when_text}</span>
          {r.current?.proof ? <span>Proof: {r.current.proof.note ?? 'file attached'}</span> : null}
        </div>
      </div>
      {edit ? (
        <>
          <select className="in" style={{ width: 'auto', padding: '4px 6px' }} value={r.every} onChange={(e) => setEvery(e.target.value as RoutineEvery)} disabled={busy} aria-label="How often">
            {ROUTINE_EVERY.map((k) => (
              <option key={k} value={k}>
                {ROUTINE_EVERY_LABEL[k]}
              </option>
            ))}
          </select>
          {r.every === 'day' ? null : r.every === 'month' ? (
            <input
              className="in"
              type="number"
              min={1}
              max={28}
              style={{ width: 64, padding: '4px 6px' }}
              value={day}
              title="Day of the month"
              aria-label="Day of the month"
              disabled={busy}
              onChange={(e) => setDay(e.target.value)}
              onBlur={commitDay}
              onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            />
          ) : (
            <select
              className="in"
              style={{ width: 'auto', padding: '4px 6px' }}
              value={r.weekday ?? 'Mon'}
              disabled={busy}
              aria-label="Day of the week"
              onChange={(e) => patch({ weekday: e.target.value as Weekday })}
            >
              {WEEKDAYS.map((d) => (
                <option key={d}>{d}</option>
              ))}
            </select>
          )}
        </>
      ) : (
        <span className="chip">{ROUTINE_EVERY_LABEL[r.every]}</span>
      )}
      <span className={`chip ${st}`}>{label}</span>
      {edit ? (
        <button
          className="btn sm ghost"
          disabled={busy}
          onClick={() => remove.mutate({ clientId, id: r.id }, { onSuccess: () => toast('Removed. It will not repeat.'), onError: fail })}
        >
          Remove
        </button>
      ) : null}
    </div>
  );
}

export function RoutineDrawer({ clientId, clientName, onClose, onOpenChecklist }: { clientId: string; clientName: string; onClose: () => void; onOpenChecklist?: () => void }) {
  const { readOnly } = useSession();
  const q = useRoutines(clientId);
  const [adding, setAdding] = useState(false);
  const [bulk, setBulk] = useState(false);
  const d = q.data;
  const edit = !!d?.can_edit && !readOnly;
  const groups = d ? ROUTINE_EVERY.map((k) => [k, d.routines.filter((r) => r.every === k)] as const).filter(([, l]) => l.length) : [];

  return (
    <>
      <Drawer title={`Routine for ${d?.client.name ?? clientName}`} onClose={onClose}>
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        ) : (
          <>
            <div className="note" style={{ marginBottom: 14 }}>
              What <b>{d!.client.spoc.name}</b> does for {d!.client.name} on a schedule. A fresh copy appears in their plan and checklist when it is due: each
              morning for daily tasks, each Monday for weekly and fortnightly, and on the set day for monthly. Each one needs proof when it is ticked.
            </div>
            <div className="cols c21">
              <div className="panel">
                <div className="hd">
                  <h3>Recurring tasks</h3>
                  <div className="r">
                    {edit ? (
                      <>
                        <button className="btn sm pri" onClick={() => setAdding(true)}>
                          + Add a recurring task
                        </button>
                        <button className="btn sm" onClick={() => setBulk(true)}>
                          For many clients
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
                {groups.length ? (
                  groups.map(([k, l]) => (
                    <div key={k}>
                      <div className="lbl" style={{ padding: '12px 16px 4px' }}>
                        {ROUTINE_EVERY_LABEL[k]} · {l.length}
                      </div>
                      {l.map((r) => (
                        <RoutineRow key={`${r.id}:${r.version}`} r={r} clientId={clientId} edit={edit} />
                      ))}
                    </div>
                  ))
                ) : (
                  <Empty title="Nothing recurring yet">Add what the SPOC must do on a schedule.</Empty>
                )}
              </div>
              <div className="panel" style={{ alignSelf: 'start' }}>
                <div className="hd">
                  <h3>Standard monthly list</h3>
                </div>
                {d!.monthly_items.length ? (
                  d!.monthly_items.map((i) => (
                    <div className="li" key={i.id}>
                      <div className="b">
                        <div className="t" style={{ fontWeight: 500 }}>
                          {i.text}
                        </div>
                      </div>
                      <span className="chip">Day {i.due_day_of_month}</span>
                    </div>
                  ))
                ) : (
                  <Empty title="No monthly checklist yet" />
                )}
                {onOpenChecklist ? (
                  <div className="bd">
                    <button className="btn sm" onClick={onOpenChecklist}>
                      Open checklists
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </>
        )}
      </Drawer>
      {adding && d ? <AddRoutineModal client={d.client} onClose={() => setAdding(false)} /> : null}
      {bulk ? <BulkRoutinesModal onClose={() => setBulk(false)} /> : null}
    </>
  );
}

// ---------------------------------------------------------------- choose clients (shared with "Give to clients")

export function ClientPicker({ clients, value, onChange }: { clients: ClientRow[]; value: Set<string>; onChange: (s: Set<string>) => void }) {
  // The segments these clients have, in the design's fixed order (prototype bkPick: BFSI, QSR, Retail).
  const segments = Segment.options.filter((s) => clients.some((c) => c.segment === s));
  const group = (g: string) => {
    const next = new Set(value);
    for (const c of clients) {
      if (g === 'none') next.delete(c.id);
      else if (g === 'all' || (g === 'high' ? c.priority === 'high' : c.segment === g)) next.add(c.id);
    }
    onChange(next);
  };
  const toggle = (id: string, on: boolean) => {
    const next = new Set(value);
    if (on) next.add(id);
    else next.delete(id);
    onChange(next);
  };
  return (
    <>
      <div className="lbl" style={{ margin: '6px 0' }}>
        Clients
      </div>
      <div className="bar" style={{ marginBottom: 8 }}>
        {[['all', 'All'], ...segments.map((s) => [s, s]), ['high', 'High priority'], ['none', 'Clear']].map(([k, l]) => (
          <button key={k} type="button" className="pill" onClick={() => group(k!)}>
            {l}
          </button>
        ))}
      </div>
      {clients.length ? (
        <div className="ckpick" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '6px 14px' }}>
          {clients.map((c) => (
            <label key={c.id} style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 13.5 }}>
              <input type="checkbox" checked={value.has(c.id)} onChange={(e) => toggle(c.id, e.target.checked)} />
              {/* Marker, name and meta are separate flex children, so the label's gap spaces them (prototype bkPick). */}
              <PrioMark priority={c.priority} />
              <span>{c.name}</span>
              <span className="sub">
                {c.segment}, {firstName(c.spoc.name)}
              </span>
            </label>
          ))}
        </div>
      ) : (
        <Empty title="No clients to choose from" />
      )}
    </>
  );
}

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

// ---------------------------------------------------------------- Recurring tasks for many clients

export function BulkRoutinesModal({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<'pick' | 'file'>('pick');
  const scope = useScopeClients();
  const bulk = useBulkRoutines();
  const { errs, setErrs, fail, toast } = useFail(WHEN_FIELDS);
  const [pickKey] = useState(newIdempotencyKey);
  const [what, setWhat] = useState('');
  const [when, setWhen] = useState<When>(NEW_WHEN);
  const [sel, setSel] = useState<Set<string>>(new Set());
  // sheet
  const [rows, setRows] = useState<SheetRow[] | null>(null);
  const [preview, setPreview] = useState<BulkRoutinesResponse | null>(null);
  const [readNote, setReadNote] = useState<string | null>(null);
  const [sheetKey, setSheetKey] = useState(newIdempotencyKey);

  const addPicked = () => {
    const local: Record<string, string> = {};
    if (what.trim().length < 3) local.what = 'Say what the SPOC does (at least 3 characters)';
    const d = dayIssue(when);
    if (d) local.day_of_month = d;
    if (!sel.size) local.client_ids = 'Choose at least one client';
    setErrs(local);
    if (Object.keys(local).length) return;
    bulk.mutate(
      { key: pickKey, body: { mode: 'pick', what: what.trim(), ...whenBody(when), client_ids: [...sel] } },
      {
        onSuccess: (r) => {
          const had = r.skipped.filter((s) => s.reason === 'already_has_it').length;
          const other = r.skipped.length - had;
          toast(
            `Added to ${plural(r.created.length, 'client')}${had ? `, ${had} already had it` : ''}${other ? `, ${other} skipped` : ''}. Repeats ${ROUTINE_EVERY_LABEL[when.every].toLowerCase()}.`,
          );
          onClose();
        },
        onError: fail,
      },
    );
  };

  const readFile = async (f: File | undefined) => {
    setPreview(null);
    setRows(null);
    setReadNote(null);
    if (!f) return;
    let parsed: SheetRow[];
    try {
      parsed = routineSheetRows(await readSheet(f));
    } catch {
      toast('Could not read that file. Save it as .xlsx or .csv and try again.');
      return;
    }
    if (!parsed.length) {
      setReadNote('No rows found in that file.');
      return;
    }
    setRows(parsed);
    bulk.mutate({ key: newIdempotencyKey(), body: { mode: 'sheet', rows: parsed, dry_run: true } }, { onSuccess: (r) => setPreview(r), onError: fail });
  };

  const rowView = (rows ?? []).map((row, i) => {
    const n = i + 1;
    const created = (preview?.created ?? []).filter((c) => c.what.trim().toLowerCase() === row.task.trim().toLowerCase());
    const skipped = (preview?.skipped ?? []).filter((s) => s.row === n);
    return { row, created, skipped };
  });
  const ready = rowView.filter((r) => r.created.length).length;

  const applySheet = () => {
    if (!rows) return;
    bulk.mutate(
      { key: sheetKey, body: { mode: 'sheet', rows, dry_run: false } },
      {
        onSuccess: (r) => {
          const had = r.skipped.filter((s) => s.reason === 'already_has_it').length;
          const tasks = new Set(r.created.map((c) => `${c.what}|${c.every}`)).size;
          toast(`${plural(tasks, 'task')} added to ${plural(r.created.length, 'client routine')}${had ? `, ${had} skipped because they already existed` : ''}.`);
          setSheetKey(newIdempotencyKey());
          onClose();
        },
        onError: fail,
      },
    );
  };

  // "Download a template" — example rows from the caller's own clients; every cell guarded and quoted (sf-7924e87f).
  const templateHref = useMemo(() => 'data:text/csv;charset=utf-8,' + encodeURIComponent(routineTemplateCsv(scope.data)), [scope.data]);

  const footer =
    tab === 'pick' ? (
      <>
        <span className="sub">{errs.client_ids && !sel.size ? <span style={{ color: 'var(--cold)' }}>{errs.client_ids}</span> : `${plural(sel.size, 'client')} chosen`}</span>
        <div className="grow" />
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn pri" onClick={addPicked} disabled={bulk.isPending}>
          {bulk.isPending ? 'Adding…' : 'Add to routines'}
        </button>
      </>
    ) : (
      <>
        <span className="sub">{rows && preview ? `${ready} of ${rows.length} rows ready` : ''}</span>
        <div className="grow" />
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn pri" onClick={applySheet} disabled={!preview || !ready || bulk.isPending}>
          {bulk.isPending && preview ? 'Adding…' : `Add ${plural(ready, 'task')}`}
        </button>
      </>
    );

  return (
    <Modal
      wide
      title="Recurring tasks for many clients"
      sub={`Each task goes into the routine of every client you choose, and is given to that client’s SPOC.${scope.any ? '' : ' You can only choose your own clients.'}`}
      onClose={onClose}
      footer={footer}
    >
      <div style={{ marginBottom: 12 }}>
        <Seg<'pick' | 'file'>
          options={[
            ['pick', 'Pick clients'],
            ['file', 'Upload a sheet'],
          ]}
          value={tab}
          onChange={setTab}
        />
      </div>
      {tab === 'pick' ? (
        <>
          <Field label="What does the SPOC do?" error={errs.what}>
            <input className="in" value={what} onChange={(e) => {
            setWhat(e.target.value);
            if (errs.what) setErrs(({ what: _w, ...rest }) => rest);
          }} placeholder="e.g. Send the weekly status to the client" maxLength={200} aria-invalid={!!errs.what || undefined} />
          </Field>
          <EveryPicker value={when} onChange={setWhen} errs={errs} />
          {scope.isPending ? <Loading label="Loading clients…" /> : scope.isError ? <ErrorState error={scope.error} onRetry={() => void scope.refetch()} /> : <ClientPicker clients={scope.data} value={sel} onChange={setSel} />}
        </>
      ) : (
        <>
          <div className="note" style={{ marginBottom: 12 }}>
            One row per task. Columns: <b>Task</b>, <b>Frequency</b> (daily, weekly, every 2 weeks, monthly), <b>Day</b> (Mon to Fri, or a date for monthly), <b>Clients</b> (All, a segment
            like BFSI, High priority, or client names separated by ; ).{' '}
            <a href={templateHref} download="recurring-tasks-template.csv" style={{ color: 'var(--brand)', fontWeight: 600 }}>
              Download a template
            </a>
          </div>
          <Field label="Excel or CSV file">
            <input type="file" accept=".xlsx,.csv,.tsv,.txt" className="in" onChange={(e) => void readFile(e.target.files?.[0])} />
          </Field>
          {readNote ? <div className="note warn">{readNote}</div> : null}
          {rows && !preview && bulk.isPending ? <Loading label="Checking the rows…" /> : null}
          {rows && preview ? (
            <div className="panel" style={{ margin: '8px 0 12px', overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ minWidth: 160 }}>Task</th>
                    <th>How often</th>
                    <th style={{ minWidth: 180 }}>Clients</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rowView.map(({ row, created, skipped }, i) => (
                    <tr key={i}>
                      <td>
                        <b>{row.task || '(missing)'}</b>
                      </td>
                      <td>
                        {row.frequency || '-'}
                        {row.day ? `, ${/^\d+$/.test(row.day) ? `day ${row.day}` : row.day}` : ''}
                      </td>
                      <td>
                        {created.length ? (
                          <>
                            {plural(created.length, 'client')}
                            <div className="sub">{created.map((c) => c.client_name).join(', ')}</div>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {/* Prototype bkRead: the warm chip with the notes when there are any, else hot "Ready". */}
                          {skipped.length ? (
                            <span className="chip warm" style={{ whiteSpace: 'normal' }}>
                              {skipped.map((s) => s.detail).join('. ')}
                            </span>
                          ) : created.length ? (
                            <span className="chip hot">Ready</span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      )}
    </Modal>
  );
}
